import { useEffect, useState } from "react";
import { DeviceMobile, X, AppleLogo, AndroidLogo, DesktopTower, ShareNetwork, DotsThreeVertical, Plus } from "@phosphor-icons/react";

/**
 * InstallAppBanner — a one-time onboarding nudge shown on the client dashboard
 * that walks new members through adding the Lazy Lean Method™ PWA to their
 * phone's home screen. Auto-detects iOS / Android / Desktop for the right steps.
 *
 * Hides itself for 30 days once dismissed (localStorage), or forever once the
 * user is already running the app in standalone/PWA mode.
 */
const DISMISS_KEY = "lazylean_install_banner_dismissed_until";
const DISMISS_DAYS = 30;

function detectPlatform() {
  if (typeof window === "undefined") return "unknown";
  const ua = window.navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function InstallAppBanner() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    if (isStandalone()) { setVisible(false); return; }
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil && Date.now() < dismissedUntil) { setVisible(false); return; }
    setVisible(true);
  }, []);

  const dismiss = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
    setOpen(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-app-banner"
        className="mb-6 border border-[#FF3333] bg-[#FF3333]/5 px-4 py-3 flex items-center gap-3"
      >
        <DeviceMobile size={22} weight="fill" className="text-[#FF3333] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-oswald text-sm uppercase tracking-wide text-white leading-tight">
            Save the app to your home screen
          </div>
          <div className="text-xs text-zinc-400 font-manrope mt-0.5">
            10-second setup · opens like a real app · no App Store needed
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="install-app-show-me"
          className="text-[10px] font-oswald uppercase tracking-[0.15em] font-bold border border-[#FF3333] text-[#FF3333] px-3 py-1.5 hover:bg-[#FF3333] hover:text-white transition-colors shrink-0"
        >
          Show me how
        </button>
        <button
          onClick={dismiss}
          data-testid="install-app-dismiss"
          aria-label="Dismiss"
          className="text-zinc-500 hover:text-white shrink-0"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {open && (
        <div
          data-testid="install-app-modal"
          className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-[#0F0F0F] border border-[#262626] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
              <div className="id-label text-[#FF3333] flex items-center gap-2">
                <DeviceMobile size={14} weight="fill" /> Install the App
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="install-app-close"
                className="text-zinc-400 hover:text-white"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="px-5 py-5">
              {platform === "ios" && <IosInstructions />}
              {platform === "android" && <AndroidInstructions />}
              {platform === "desktop" && <DesktopInstructions />}

              <div className="mt-6 pt-4 border-t border-[#262626]">
                <details>
                  <summary className="text-xs font-manrope text-zinc-500 uppercase tracking-[0.15em] cursor-pointer hover:text-white">
                    Using a different device?
                  </summary>
                  <div className="mt-4 space-y-4">
                    {platform !== "ios" && <IosInstructions compact />}
                    {platform !== "android" && <AndroidInstructions compact />}
                    {platform !== "desktop" && <DesktopInstructions compact />}
                  </div>
                </details>
              </div>

              <button
                onClick={dismiss}
                data-testid="install-app-done"
                className="mt-6 w-full id-btn-primary"
              >
                Got it, thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------- Per-platform steps ----------------------

function IosInstructions({ compact = false }) {
  return (
    <div data-testid="install-ios-steps">
      <div className="flex items-center gap-2 mb-3">
        <AppleLogo size={18} weight="fill" className="text-white" />
        <div className="font-oswald text-lg uppercase tracking-wide text-white">iPhone / iPad</div>
      </div>
      {!compact && (
        <p className="text-sm text-zinc-400 font-manrope mb-4">
          Works in Safari only — not Chrome or other browsers on iOS.
        </p>
      )}
      <Step
        n={1}
        title="Open Safari"
        body="Make sure you&rsquo;re on lazyleanmethod.com in the Safari browser (not Chrome, Firefox, or in-app browsers)."
      />
      <Step
        n={2}
        title="Tap the Share icon"
        body={<>Bottom of the screen (middle) — the square with an arrow pointing up.</>}
        icon={<ShareNetwork size={16} weight="bold" className="text-[#FF3333]" />}
      />
      <Step
        n={3}
        title="Scroll & tap &lsquo;Add to Home Screen&rsquo;"
        body="Scroll down in the Share menu until you see it. Sometimes near the bottom of the list."
        icon={<Plus size={16} weight="bold" className="text-[#FF3333]" />}
      />
      <Step
        n={4}
        title="Tap Add"
        body="Top-right corner. The icon lands on your home screen — tap it to open the app like any other."
      />
    </div>
  );
}

function AndroidInstructions({ compact = false }) {
  return (
    <div data-testid="install-android-steps">
      <div className="flex items-center gap-2 mb-3">
        <AndroidLogo size={18} weight="fill" className="text-white" />
        <div className="font-oswald text-lg uppercase tracking-wide text-white">Android</div>
      </div>
      {!compact && (
        <p className="text-sm text-zinc-400 font-manrope mb-4">
          Works best in Chrome. Samsung Internet works too.
        </p>
      )}
      <Step
        n={1}
        title="Open Chrome"
        body="Make sure you&rsquo;re on lazyleanmethod.com in Chrome (or Samsung Internet)."
      />
      <Step
        n={2}
        title="Tap the 3-dot menu"
        body="Top-right corner of Chrome."
        icon={<DotsThreeVertical size={18} weight="bold" className="text-[#FF3333]" />}
      />
      <Step
        n={3}
        title="Tap &lsquo;Install app&rsquo; or &lsquo;Add to Home Screen&rsquo;"
        body="Depending on your Chrome version, it&rsquo;ll say one or the other."
        icon={<Plus size={16} weight="bold" className="text-[#FF3333]" />}
      />
      <Step
        n={4}
        title="Tap Install / Add"
        body="The Lazy Lean icon lands on your home screen. Open it — it behaves just like a native app."
      />
    </div>
  );
}

function DesktopInstructions({ compact = false }) {
  return (
    <div data-testid="install-desktop-steps">
      <div className="flex items-center gap-2 mb-3">
        <DesktopTower size={18} weight="fill" className="text-white" />
        <div className="font-oswald text-lg uppercase tracking-wide text-white">Desktop</div>
      </div>
      {!compact && (
        <p className="text-sm text-zinc-400 font-manrope mb-4">
          Chrome, Edge, and Brave support it. Firefox and Safari on Mac don&rsquo;t (bookmark it instead).
        </p>
      )}
      <Step
        n={1}
        title="Open in Chrome, Edge or Brave"
        body="Go to lazyleanmethod.com in one of these browsers."
      />
      <Step
        n={2}
        title="Look for the install icon in the address bar"
        body="Right side of the URL bar — a small monitor icon with a downward arrow."
        icon={<Plus size={16} weight="bold" className="text-[#FF3333]" />}
      />
      <Step
        n={3}
        title="Click Install"
        body="A popup asks &lsquo;Install Lazy Lean Method?&rsquo; — click Install. A shortcut lands on your dock/desktop."
      />
    </div>
  );
}

function Step({ n, title, body, icon = null }) {
  return (
    <div className="flex gap-4 mb-4 last:mb-0">
      <div className="flex-shrink-0 w-8 h-8 border border-[#FF3333] flex items-center justify-center font-oswald text-lg text-[#FF3333]">
        {n}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="font-oswald text-sm uppercase tracking-wide text-white flex items-center gap-2">
          {title}
          {icon}
        </div>
        <p className="text-sm text-zinc-400 font-manrope mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
